import React from 'react'
import { useCarouselDragging } from './use-dragging'
import { useCarouselKeyboard } from './use-keyboard'
import { useCarouselContext } from './carousel-context'
import { composeRefs } from '../utils/compose-refs'
import { forwardRefType } from '../utils/forward-ref-type'

export interface SlideContainerProps {
  /**
   * optional callback function
   */
  onDragStart?: (e: React.TouchEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => void

  /**
   * optional callback function
   */
  onDrag?: (e: React.TouchEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => void

  /**
   * optional callback function
   */
  onDragEnd?: (e: React.TouchEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => void

  /**
   * Callback called when user-triggered navigation occurs: dragging/swiping,
   * clicking one of the controls (custom controls not included), or using a
   * keyboard shortcut
   */
  onUserNavigation?: (e: React.TouchEvent | React.MouseEvent | React.KeyboardEvent) => void

  /**
   * Enable mouse swipe/dragging
   */
  dragging?: boolean

  /**
   * The percentage (from 0 to 1) of a slide that the user needs to drag before
   * @default `0.5`
   */
  dragThreshold?: number

  /**
   * Enable touch swipe/dragging
   */
  swiping?: boolean
  /**
   * When set to true, disable keyboard controls
   * @default false
   */
  enableKeyboardControls?: boolean
  /**
   * When enableKeyboardControls is enabled, Configure keyCodes for corresponding slide actions as array of keyCodes
   */
  keyCodeConfig?: {
    firstSlide?: number[]
    lastSlide?: number[]
    nextSlide?: number[]
    previousSlide?: number[]
  }
  children?: React.ReactNode

  /**
   * Animation easing function accepting a normalized time between 0 and 1,
   * inclusive, and returning an eased time, which equals 0 at normalizedTime==0
   * and equals 1 at normalizedTime==1
   */
  easing?: (normalizedTime: number) => number

  /**
   * Animation easing function when swipe exceeds edge accepting a normalized time between 0 and 1,
   * inclusive, and returning an eased time, which equals 0 at normalizedTime==0
   * and equals 1 at normalizedTime==1
   */
  edgeEasing?: (normalizedTime: number) => number

  /**
   * Disable swipe before first slide and after last slide
   * @default false
   */
  disableEdgeSwiping?: boolean

  className?: string
}

const easeOut = (t: number) => (t - 1) ** 3 + 1

function SlideContainerRaw(
  {
    onDragStart = () => {},
    onDrag = () => {},
    onDragEnd = () => {},
    onUserNavigation = () => {},
    dragging: desktopDraggingEnabled = true,
    dragThreshold = 0.5,
    swiping: mobileDraggingEnabled = true,
    enableKeyboardControls = false,
    keyCodeConfig = {
      nextSlide: [39, 68, 38, 87],
      previousSlide: [37, 65, 40, 83],
      firstSlide: [81],
      lastSlide: [69]
    },
    children,
    easing = easeOut,
    edgeEasing = easeOut,
    className
  }: SlideContainerProps,
  ref: React.Ref<HTMLDivElement>,
  disableEdgeSwiping = false
) {
  const { slideContainerRef, frameHeight } = useCarouselContext()
  const { onMouseUp, onMouseLeave, onTouchEnd, onMouseDown, onMouseMove, onTouchStart, onTouchMove, positioning } =
    useCarouselDragging({
      onDragStart,
      onDragEnd,
      onDrag,
      onUserNavigation,
      dragThreshold,
      mobileDraggingEnabled,
      desktopDraggingEnabled,
      easing,
      edgeEasing,
      disableEdgeSwiping
    })

  const { onKeyDown } = useCarouselKeyboard({
    keyCodeConfig,
    onUserNavigation
  })

  return (
    <SlideContainerContext.Provider value={{ positioning }}>
      <div
        style={{
          overflow: 'hidden',
          width: '100%',
          position: 'relative',
          outline: 'none',
          touchAction: 'pan-y',
          height: frameHeight,
          willChange: 'height',
          userSelect: 'none'
        }}
        tabIndex={enableKeyboardControls ? 0 : -1}
        onKeyDown={enableKeyboardControls ? onKeyDown : undefined}
        ref={composeRefs(slideContainerRef, ref)}
        onMouseUp={onMouseUp}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onTouchMove={onTouchMove}
        className={className}
      >
        {children}
      </div>
    </SlideContainerContext.Provider>
  )
}

export const SlideContainer = (React.forwardRef as forwardRefType)(SlideContainerRaw)

export const SlideContainerContext = React.createContext<{ positioning: string | undefined }>({ positioning: undefined })
